// @flow

/**
 * @module apollo-client
 *
 * Apollo boost is convenient but hides details. We would be changing the client
 * details, like persistant storage, and so being explicit about how its setup.
 */
import ApolloClient from 'apollo-client';
import { ApolloLink } from 'apollo-link';
import * as Sentry from '@sentry/browser';
import { HttpLink } from 'apollo-link-http';
import {
  InMemoryCache,
  defaultDataIdFromObject,
  IntrospectionFragmentMatcher,
} from 'apollo-cache-inmemory';
import { onError } from 'apollo-link-error';
import { setContext } from 'apollo-link-context';

import { decodeToken, getToken, clearStoredSession } from './session';
import mockClient from './apolloMockClient';
import { APP_NAME, REQUEST_HEADERS } from './constants/enums';

// generated by Fragment Matcher plugin
import fragmentIntrospection from './fragment-introspection';

const fragmentMatcher = new IntrospectionFragmentMatcher({
  introspectionQueryResultData: fragmentIntrospection,
});

const cache = new InMemoryCache({
  fragmentMatcher,
  dataIdFromObject: (object) => {
    switch (object.__typename) {
      case 'Authorization':
        return `${object.__typename}:${object.retailerOutletId}-${object.retailerId}`;
      case 'ProductEntity':
        return `${object.__typename}:${object.companyId}-${object.type}-${object.id}`;
      case 'SkunitProperty':
        return `${object.__typename}:${object.companyId}-${object.id}`;
      case 'SkuSearchResponse':
        return `${object.__typename}:${object.companyId}-${object.type}-${object.typeId}`;
      case 'SkuFilterValues':
        return `${object.__typename}:${object.id}-${object.name}`;
      case 'WarehouseDetails':
        return `${object.__typename}:${object.id}-${object.name}`;
      case 'GRNDetails':
        return `${object.__typename}:${object.id}-${object.companyId}`;
      case 'RewardSummary':
        return `${object.__typename}:${object.globalOutletId}-${object.companyId}-${object.type}`;
      case 'ContactDetail':
        return `${object.__typename}:${object.companyId}-${object.entity}-${object.entityId}`;
      case 'Reason':
        return `${object.__typename}:${object.companyId}-${object.id}`;
      default:
        return defaultDataIdFromObject(object);
    }
  },
});

const httpLink = new HttpLink({
  uri: `${process.env.REACT_APP_GQL_ENDPOINT || ''}/graphql`,
});

// https://www.apollographql.com/docs/react/recipes/authentication.html#Header
const authLink = setContext((_, { headers }) => {
  const token = getToken();
  const xRetKey =
    (headers && headers[REQUEST_HEADERS.X_RET_KEY]) ||
    process.env.REACT_APP_RETAILER_APP_X_RET_KEY ||
    '';

  return {
    headers: {
      ...headers,
      [REQUEST_HEADERS.X_RET_KEY]: `${xRetKey}`,
      [REQUEST_HEADERS.AUTHORIZATION]:
        (headers && headers[REQUEST_HEADERS.AUTHORIZATION]) ||
        (token ? `Bearer ${token}` : ''),
    },
  };
});

const errorLink = onError(
  ({ operation, response, graphQLErrors, networkError }) => {
    if (graphQLErrors) {
      const decodedToken = decodeToken();
      const headers = operation.getContext().response.headers;
      const transactionId = headers.get('x-request-id');

      graphQLErrors.forEach((err) => {
        Sentry.withScope((scope) => {
          scope.setTag('appName', APP_NAME);
          scope.setTag('operationName', operation.operationName);
          scope.setUser({
            ID: (decodedToken && decodedToken.userId) || null,
            APP_NAME: APP_NAME,
            CLIENT_ID: (decodedToken && decodedToken.clientId) || null,
          });

          if (err.path) {
            scope.addBreadcrumb({
              category: 'query-path',
              message: `${err.path.join(' > ')} > ${err.message}`,
            });
          }

          if (transactionId) {
            scope.setTransaction(transactionId);
          }

          Sentry.captureException(err);
        });
      });
    }

    if (
      graphQLErrors &&
      graphQLErrors.some(
        ({ message }) => message.indexOf('Invalid Token') !== -1
      )
    ) {
      clearStoredSession();
      location.reload(); // eslint-disable-line no-restricted-globals
      return;
    }

    if (networkError) console.log(`[Network error]: ${networkError}`);
  }
);

const client = process.env.REACT_APP_MOCK
  ? mockClient
  : new ApolloClient<any>({
      link: ApolloLink.from([errorLink, authLink, httpLink]),
      cache,
      connectToDevTools: true,
    });

export default client;
